/**
 * Level 3, Lesson 2 skills (lesson2_eights.tex): adding 8 ("back 2, down one row"), subtracting 8 from a teen
 * ("the answer is 2 more than the digit above the 8"), 3-digit renaming, and the one-step story problems.
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { mapPuzzle } from "../visuals";
import { add3Vertical, sub3Vertical } from "./columns";

const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);

/** Adding 9 or 8 on the Map: back the bond, then down a row. */
export function addMapItem(add: 9 | 8, n: number): Item {
  const bond = 10 - add, ans = n + add;
  return {
    id: `add-n-map-puzzle:${n}+${add}`,
    skill: add === 9 ? "add-n-map-puzzle" : "add-n-map-puzzle/8",
    prompt: `${n} + ${add} =`,
    visual: mapPuzzle(n, bond),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      if (hit(raw, n + 10)) return { code: "addmap-no-back", hint: `You went down a row. Go back ${bond} first.` };
      if (hit(raw, n + 10 + bond)) return { code: "addmap-forward", hint: `Back ${bond}, not forward. Then down a row.` };
      if (hit(raw, n - bond)) return { code: "addmap-no-down", hint: `That's back ${bond}. Now go down a row.` };
      if (add === 8 && hit(raw, n + 9)) return { code: "addmap-wrong-bond", hint: "Back 2 for the eights. Eight's bond is 2." };
      return null;
    },
    hints: [`Start at ${n}. Go back ${bond}. Where are you?`, "Now go down one row, same column.", `${n}: back ${bond} is ${n - bond}, down a row is ${ans}.`],
  };
}

export const add8: Skill = {
  id: "add-n-map-puzzle/8",
  name: "Add 8: back 2 and down",
  gen: (rng, level) => {
    // The book's starts: top-line 2–9, or two-digit with a ones digit of 2 or more (so "back 2" stays on the line).
    if (level === 0 && rng() < 0.5) return addMapItem(8, int(rng, 2, 9));
    let n = int(rng, 12, 89);
    while (n % 10 < 2) n = int(rng, 12, 89);
    return addMapItem(8, n);
  },
};

export function sub8Item(t: number): Item {
  const ones = t - 10, ans = t - 8;
  return {
    id: `sub-8-teen:${t}`,
    skill: "sub-8-teen",
    prompt: `${t} − 8 =`,
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      if (hit(raw, ones)) return { code: "sub8-no-two", hint: `That's the digit above the 8. The answer is 2 more than it.` };
      if (8 - ones !== ans && hit(raw, 8 - ones)) return { code: "sub-smaller-from-larger", hint: `${ones} − 8 doesn't work in the ones. Use the pattern: 2 more than the digit above the 8.` };
      if (hit(raw, ones - 2)) return { code: "sub8-two-less", hint: "2 MORE than the digit above the 8, not 2 less." };
      return null;
    },
    hints: [`Look at the digit above the 8: ${ones}.`, "The answer is 2 more than that digit.", `The digit above the 8 is ${ones}; 2 more is ${ans}. ${t} − 8 = ${ans}.`],
  };
}
export const sub8: Skill = { id: "sub-8-teen", name: "Subtract 8 from a teen", gen: (rng) => sub8Item(int(rng, 11, 17)) };

/** One-step story problems with 3-digit numbers (L3.2: Marisol's cans, the theater's seats). */
export const word3: Skill = {
  id: "word-add-sub-3digit",
  name: "Story problems, 3-digit",
  gen: (rng) => {
    const add = rng() < 0.5;
    if (add) {
      const a = int(rng, 215, 489), b = int(rng, 186, 459);
      const s = pick(rng, [
        [`The school collected ${a} cans in May and ${b} cans in June. How many cans in all?`, "cans"],
        [`A library has ${a} picture books and ${b} chapter books. How many books in all?`, "books"],
        [`On Saturday ${a} people came to the fair. On Sunday ${b} came. How many people came in all?`, "people"],
      ] as const);
      return {
        id: `word-add-sub-3digit:${a}+${b}`, skill: "word-add-sub-3digit", prompt: s[0], input: { kind: "number", label: s[1] }, answer: String(a + b),
        diagnose: (raw) => (hit(raw, Math.abs(a - b)) ? { code: "word-wrong-op", hint: `"In all" puts the two groups together. Add them.` } : null),
        hints: ["Two groups put together: add or subtract?", "\"In all\" means put together — add. Ones first, rename when you need to.", `${a} + ${b} = ${a + b} ${s[1]}.`],
      };
    }
    const total = pick(rng, [300, 400, 500, 600, 700, 800]), part = int(rng, 112, total - 60);
    const s = pick(rng, [
      [`A theater has ${total} seats. ${part} are filled. How many seats are empty?`, "seats"],
      [`A farm stand had ${total} apples. It sold ${part}. How many apples are left?`, "apples"],
    ] as const);
    return {
      id: `word-add-sub-3digit:${total}-${part}`, skill: "word-add-sub-3digit", prompt: s[0], input: { kind: "number", label: s[1] }, answer: String(total - part),
      diagnose: (raw) => (hit(raw, total + part) ? { code: "word-wrong-op", hint: "Some are filled — you want the ones that are not. Take away." } : null),
      hints: ["Is this putting together or taking away?", `Start with all ${total}, then take ${part} away — across a zero, go get a ten from the hundreds first.`, `${total} − ${part} = ${total - part} ${s[1]}.`],
    };
  },
};

export const LEVEL3_L2_SKILLS: Skill[] = [add8, sub8, add3Vertical, sub3Vertical, word3];
